import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import ToastService from '../../core/notifications/ToastService';
import BookingCalendarView from '../../components/Bookings/BookingCalendarView';

interface Booking {
  id: number;
  booking_number?: string;
  bookingNumber?: string;
  user_name?: string;
  userName?: string;
  user_phone?: string;
  userPhone?: string;
  gym_name?: string;
  gymName?: string;
  booking_date?: string;
  date?: string;
  start_time?: string;
  startTime?: string;
  end_time?: string;
  endTime?: string;
  status: 'pending' | 'confirmed' | 'checked-in' | 'completed' | 'cancelled';
  check_in_time?: string;
  checkInTime?: string;
  check_out_time?: string;
  checkOutTime?: string;
  total_amount?: number;
  amount?: number;
  payment_method?: string;
  rating?: number;
}

type ViewType = 'list' | 'card' | 'calendar';

export const EnhancedBookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // View and Display
  const [viewType, setViewType] = useState<ViewType>('list');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGym, setFilterGym] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Dialogs
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showCheckOutDialog, setShowCheckOutDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch bookings
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<any>('/partners/bookings');
      const bookingsArray = Array.isArray(data) ? data : (data?.bookings || data?.data || []);
      setBookings(bookingsArray);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch bookings';
      setError(message);
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Normalize booking data
  const normalizeBooking = (booking: Booking): Booking => ({
    ...booking,
    bookingNumber: booking.booking_number || booking.bookingNumber || '',
    userName: booking.user_name || booking.userName || '',
    userPhone: booking.user_phone || booking.userPhone || '',
    gymName: booking.gym_name || booking.gymName || '',
    date: booking.booking_date || booking.date || '',
    startTime: booking.start_time || booking.startTime || '',
    endTime: booking.end_time || booking.endTime || '',
    checkInTime: booking.check_in_time || booking.checkInTime,
    checkOutTime: booking.check_out_time || booking.checkOutTime,
    amount: booking.total_amount || booking.amount || 0,
  });

  const normalizedBookings = bookings.map(normalizeBooking);

  // Apply filters
  const filteredBookings = normalizedBookings.filter((booking) => {
    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
    const matchesGym = filterGym === 'all' || booking.gymName === filterGym;
    const matchesSearch =
      booking.bookingNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.userPhone?.includes(searchQuery);
    
    return matchesStatus && matchesGym && matchesSearch;
  });

  // Apply sorting
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    let aVal: any = a[sortBy as keyof Booking];
    let bVal: any = b[sortBy as keyof Booking];
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
    }
    
    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Paginate
  const paginatedBookings = sortedBookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get unique gyms
  const uniqueGyms = [...new Set(normalizedBookings.map(b => b.gymName).filter(Boolean))];

  // Status colors
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      confirmed: '#3B82F6',
      'checked-in': '#F59E0B',
      completed: '#10B981',
      cancelled: '#EF4444',
      pending: '#14B8A6'
    };
    return colors[status] || '#6B7280';
  };

  // Check-in handler
  const handleCheckIn = async () => {
    if (!selectedBooking) return;
    try {
      setActionLoading(true);
      await apiClient.post(`/bookings/${selectedBooking.id}/check-in`, {
        check_in_method: 'manual'
      });
      setBookings(bookings.map(b =>
        b.id === selectedBooking.id ? { ...b, status: 'checked-in' as const } : b
      ));
      ToastService.success(`Check-in successful for ${selectedBooking.userName}`);
      setShowCheckInDialog(false);
      setSelectedBooking(null);
    } catch (err: any) {
      ToastService.error(err.response?.data?.message || 'Check-in failed');
    } finally {
      setActionLoading(false);
    }
  };

  // Check-out handler
  const handleCheckOut = async () => {
    if (!selectedBooking) return;
    try {
      setActionLoading(true);
      await apiClient.post(`/bookings/${selectedBooking.id}/check-out`, {});
      setBookings(bookings.map(b =>
        b.id === selectedBooking.id ? { ...b, status: 'completed' as const } : b
      ));
      ToastService.success(`Check-out successful for ${selectedBooking.userName}`);
      setShowCheckOutDialog(false);
      setSelectedBooking(null);
    } catch (err: any) {
      ToastService.error(err.response?.data?.message || 'Check-out failed');
    } finally {
      setActionLoading(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Booking #', 'User', 'Phone', 'Gym', 'Date', 'Time', 'Status', 'Amount'];
    const rows = sortedBookings.map(b => [
      b.bookingNumber,
      b.userName,
      b.userPhone,
      b.gymName,
      b.date,
      `${b.startTime} - ${b.endTime}`,
      b.status,
      b.amount
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    ToastService.success('Bookings exported to CSV');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Bookings Management
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Manage and track all gym bookings
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* View Tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: '#e0e0e0' }}>
        <Tabs value={viewType} onChange={(_, val) => setViewType(val)}>
          <Tab label="List View" value="list" />
          <Tab label="Card View" value="card" />
          <Tab label="Calendar View" value="calendar" />
        </Tabs>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by booking #, name, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="checked-in">Checked In</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Gym</InputLabel>
              <Select value={filterGym} label="Gym" onChange={(e) => setFilterGym(e.target.value)}>
                <MenuItem value="all">All Gyms</MenuItem>
                {uniqueGyms.map(gym => (
                  <MenuItem key={gym} value={gym}>{gym}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="amount">Amount</MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="userName">Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={sortedBookings.length === 0}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {filterStatus !== 'all' && (
            <Chip
              label={`Status: ${filterStatus}`}
              onDelete={() => setFilterStatus('all')}
              size="small"
            />
          )}
          {filterGym !== 'all' && (
            <Chip
              label={`Gym: ${filterGym}`}
              onDelete={() => setFilterGym('all')}
              size="small"
            />
          )}
          {searchQuery && (
            <Chip
              label={`Search: ${searchQuery}`}
              onDelete={() => setSearchQuery('')}
              size="small"
            />
          )}
        </Box>
      </Card>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : viewType === 'list' ? (
        <>
          {/* List View */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Booking #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Gym</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        No bookings found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBookings.map((booking) => (
                    <TableRow key={booking.id} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{booking.bookingNumber}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {booking.userName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {booking.userPhone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{booking.gymName}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{booking.date}</Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {booking.startTime} - {booking.endTime}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(booking.status) + '20',
                            color: getStatusColor(booking.status),
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>₹{booking.amount}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setShowDetails(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {booking.status === 'confirmed' && (
                          <Tooltip title="Check In">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowCheckInDialog(true);
                              }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {booking.status === 'checked-in' && (
                          <Tooltip title="Check Out">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowCheckOutDialog(true);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedBookings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      ) : viewType === 'card' ? (
        /* Card View */
        <Grid container spacing={2}>
          {paginatedBookings.length === 0 ? (
            <Grid item xs={12}>
              <Typography sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                No bookings found
              </Typography>
            </Grid>
          ) : (
            paginatedBookings.map((booking) => (
              <Grid item xs={12} sm={6} md={4} key={booking.id}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {booking.bookingNumber}
                      </Typography>
                      <Chip
                        label={booking.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(booking.status) + '20',
                          color: getStatusColor(booking.status)
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{booking.userName}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
                      {booking.userPhone}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{booking.gymName}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
                      {booking.date} • {booking.startTime} - {booking.endTime}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                        ₹{booking.amount}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowDetails(true);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      ) : (
        /* Calendar View */
        <BookingCalendarView 
          bookings={sortedBookings.map(b => ({
            id: b.id,
            bookingNumber: b.bookingNumber,
            userName: b.userName,
            gymName: b.gymName,
            date: b.date,
            startTime: b.startTime,
            endTime: b.endTime,
            status: b.status,
            amount: b.amount
          }))} 
          onSelectBooking={(booking) => {
            const fullBooking = sortedBookings.find(b => b.id === booking.id);
            if (fullBooking) {
              setSelectedBooking(fullBooking);
              setShowDetails(true);
            }
          }} 
        />
      )}

      {/* Check-In Dialog */}
      <Dialog open={showCheckInDialog} onClose={() => setShowCheckInDialog(false)}>
        <DialogTitle>Confirm Check-In</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Booking:</strong> {selectedBooking.bookingNumber}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>User:</strong> {selectedBooking.userName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Gym:</strong> {selectedBooking.gymName}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong> {selectedBooking.startTime} - {selectedBooking.endTime}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheckInDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCheckIn}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Check In'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-Out Dialog */}
      <Dialog open={showCheckOutDialog} onClose={() => setShowCheckOutDialog(false)}>
        <DialogTitle>Confirm Check-Out</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Booking:</strong> {selectedBooking.bookingNumber}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>User:</strong> {selectedBooking.userName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Check-In Time:</strong> {selectedBooking.checkInTime || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheckOutDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCheckOut}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Check Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Booking Details</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Booking #:</strong> {selectedBooking.bookingNumber}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>User:</strong> {selectedBooking.userName} ({selectedBooking.userPhone})
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Gym:</strong> {selectedBooking.gymName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Date:</strong> {selectedBooking.date}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Time:</strong> {selectedBooking.startTime} - {selectedBooking.endTime}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Status:</strong> {selectedBooking.status}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Amount:</strong> ₹{selectedBooking.amount}
              </Typography>
              {selectedBooking.checkInTime && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Check-In Time:</strong> {selectedBooking.checkInTime}
                </Typography>
              )}
              {selectedBooking.checkOutTime && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Check-Out Time:</strong> {selectedBooking.checkOutTime}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnhancedBookingsPage;
